import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store from '../../store/store';
import { toast } from 'react-toastify';
import {
    Typography,
    Box,
    Autocomplete,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    MenuItem,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import YearPicker from '../Masking/YearPickerNormal';
import {
    setProgressYears,
    setProgressProductivityIndex,
    setProgressProductivityDecimals,
    setProgressLandCoverIndex,
    setProgressSoilOrganicIndex,
    setProgressProductivityLandCover,
    setProgressAdvancedEnabled,
    setProgressProductivityAdvanced,
    setProgressTransformationMatrix,
    setProgressLandCoverAdvanced,
    setProgressLandCoverTransformationMatrix,
    setProgressLandCoverAdvancedEnabled,
    setProgressSoilOrganicAdvanced,
    setProgressSoilOrganicLandCoverAdvanced,
    setProgressSoilOrganicLandCoverTransformationMatrix,
    setProgressSoilOrganicLandCoverAdvancedEnabled,
    setProgressSoilOrganicTransformationMatrix,
    setProgressLandCoverIndicatorMatrix,
    setProgressSoilOrganicAdvancedEnabled,
    setProgressSoilOrganicLandCoverIndex
} from '../../reducers/LandDegradationSlice';
import InfoIcon from '@mui/icons-material/Info';
import "./styles.css";

const MatrixRowInput = ({ rowKey, value, onMatrixChange }) => {
    const [inputValue, setInputValue] = useState(value.join(', '));

    useEffect(() => {
        setInputValue(value.join(', '));
    }, [value]);

    const handleChange = (e) => {
        const newValue = e.target.value;
        if (/^[\d,\s]*$/.test(newValue)) {
            setInputValue(newValue);
        }
    };

    const handleBlur = () => {
        const parsedValues = inputValue
            .split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        onMatrixChange(rowKey, parsedValues);
        setInputValue(parsedValues.join(', '));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    return (
        <TextField
            fullWidth
            size="small"
            value={inputValue}
            placeholder="e.g., 1, 2, 3, 4"
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            helperText={`${value.length}/20 values`}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#339ace',
                        }
                    }
                }
            }}
        />
    );
};

const ProgressConfig = () => {
    const dispatch = useDispatch();

    // Progress-specific selectors - NO fallback to baseline
    const progressProductivityIndex = useSelector((state) => state.landDegradation.progressProductivityIndex);
    const progressProductivityLandCover = useSelector((state) => state.landDegradation.progressProductivityLandCover);
    const progressLandCoverIndex = useSelector((state) => state.landDegradation.progressLandCoverIndex);
    const progressSoilOrganicIndex = useSelector((state) => state.landDegradation.progressSoilOrganicIndex);
    const progressSoilOrganicLandCoverIndex = useSelector((state) => state.landDegradation.progressSoilOrganicLandCoverIndex);
    const progressProductivityDecimals = useSelector((state) => state.landDegradation.progressProductivityDecimals);

    const progressProductivityYearFrom = useSelector((state) => state.landDegradation.progressYears?.productivity?.from);
    const progressProductivityYearTo = useSelector((state) => state.landDegradation.progressYears?.productivity?.to);
    const progressLandCoverYearFrom = useSelector((state) => state.landDegradation.progressYears?.landcover?.from);
    const progressLandCoverYearTo = useSelector((state) => state.landDegradation.progressYears?.landcover?.to);
    const progressSoilOrganicYearFrom = useSelector((state) => state.landDegradation.progressYears?.soilorganic?.from);
    const progressSoilOrganicYearTo = useSelector((state) => state.landDegradation.progressYears?.soilorganic?.to);

    const [showLandCoverIndicatorMatrix, setShowLandCoverIndicatorMatrix] = useState(false);
    const progressLandCoverIndicatorMatrix = useSelector((state) => state.landDegradation.progressLandCoverIndicatorMatrix);

    const initialIndicatorMatrix = {
        treeCovered: [4, 1, 1, 1, 1, 1, 4],
        grassland: [5, 4, 5, 1, 1, 1, 4],
        cropland: [5, 1, 4, 1, 1, 1, 4],
        wetland: [1, 1, 1, 4, 1, 1, 4],
        artificialArea: [5, 5, 5, 5, 4, 5, 4],
        bareLand: [5, 5, 5, 5, 1, 4, 4],
        water: [4, 4, 4, 4, 4, 4, 4]
    };

    // Copy From Dialog States
    const [copyFromDialogOpen, setCopyFromDialogOpen] = useState(false);
    const [copySource, setCopySource] = useState('');
    const [currentMatrixType, setCurrentMatrixType] = useState('');

    const getTransitionColor = (value) => {
        switch (value) {
            case "5": return "#4CAF50";
            case "4": return "#FFFFFF";
            case "1": return "#9C27B0";
            default: return "#FFFFFF";
        }
    };

    const handleResetIndicatorTable = () => {
        dispatch(setProgressLandCoverIndicatorMatrix(initialIndicatorMatrix));
    };

    // Validation function for transformation matrix
    const validateTransformationMatrix = (matrix) => {
        if (!matrix) return { valid: false, error: "Matrix is empty" };

        const allValues = Object.values(matrix).flat();
        if (allValues.length === 0) {
            return { valid: false, error: "All fields cannot be empty" };
        }
        if (allValues.length > 20) {
            return { valid: false, error: "Maximum 20 values allowed" };
        }

        return { valid: true };
    };

    // Copy matrix from other indicators
    const handleOpenCopyDialog = (matrixType) => {
        setCurrentMatrixType(matrixType);
        setCopyFromDialogOpen(true);
        setCopySource('');
    };

    const handleCopyMatrix = () => {
        if (!copySource) {
            toast.error("Please select a source");
            return;
        }

        const state = store.getState().landDegradation;
        let sourceMatrix = null;

        switch (copySource) {
            case 'baseline-productivity':
                sourceMatrix = state.productivityAdvanced.transformationMatrix;
                break;
            case 'baseline-landcover':
                sourceMatrix = state.landCoverAdvanced.transformationMatrix;
                break;
            case 'baseline-soilorganic':
                sourceMatrix = state.soilOrganicLandCoverAdvanced.transformationMatrix;
                break;
            case 'progress-productivity':
                sourceMatrix = state.progressProductivityAdvanced.transformationMatrix;
                break;
            case 'progress-landcover':
                sourceMatrix = state.progressLandCoverAdvanced.transformationMatrix;
                break;
            case 'progress-soilorganic':
                sourceMatrix = state.progressSoilOrganicLandCoverAdvanced.transformationMatrix;
                break;
        }

        if (sourceMatrix) {
            switch (currentMatrixType) {
                case 'productivity':
                    dispatch(setProgressTransformationMatrix(sourceMatrix));
                    break;
                case 'landcover':
                    dispatch(setProgressLandCoverTransformationMatrix(sourceMatrix));
                    break;
                case 'soilorganic':
                    dispatch(setProgressSoilOrganicLandCoverTransformationMatrix(sourceMatrix));
                    break;
            }
            toast.success("Matrix copied successfully");
        } else {
            toast.error("Source matrix not found or is empty");
        }
        setCopyFromDialogOpen(false);
    };

    const handleClearMatrix = (matrixType) => {
        const emptyMatrix = {
            treeCovered: [],
            grassland: [],
            cropland: [],
            wetland: [],
            artificialArea: [],
            bareLand: [],
            water: []
        };

        switch (matrixType) {
            case 'productivity':
                dispatch(setProgressTransformationMatrix(emptyMatrix));
                break;
            case 'landcover':
                dispatch(setProgressLandCoverTransformationMatrix(emptyMatrix));
                break;
            case 'soilorganic':
                dispatch(setProgressSoilOrganicLandCoverTransformationMatrix(emptyMatrix));
                break;
        }
        toast.info("Matrix cleared");
    };

    useEffect(() => {
        if (showLandCoverIndicatorMatrix) {
            if (!progressLandCoverIndicatorMatrix) {
                dispatch(setProgressLandCoverIndicatorMatrix(initialIndicatorMatrix));
            }
        } else {
            dispatch(setProgressLandCoverIndicatorMatrix(null));
        }
    }, [showLandCoverIndicatorMatrix, dispatch]);

    const getIndexAvailableFrom = (indexId, source = null) => {
        try {
            const categoriesStr = localStorage.getItem('categories');
            if (!categoriesStr) return 2000;

            const categories = JSON.parse(categoriesStr);
            const selectedIndex = categories.find(category => category.id === indexId);

            if (source) {
                if (selectedIndex?.name && selectedIndex.name.toLowerCase().includes('esa cci')) {
                    if (source === "productivity") {
                        return 2005;
                    }
                    return 2015;
                }
            }

            if (selectedIndex && selectedIndex.available_from) {
                const fromDate = new Date(selectedIndex.available_from);
                return fromDate.getFullYear();
            }
            return 2000;
        } catch (error) {
            console.error('Error getting available_from:', error);
            return 2000;
        }
    };

    const getIndexAvailableTo = (indexId, source = null) => {
        try {
            const categoriesStr = localStorage.getItem('categories');
            if (!categoriesStr) return 2022;

            const categories = JSON.parse(categoriesStr);
            const selectedIndex = categories.find(category => category.id === indexId);

            if (source) {
                if (selectedIndex?.name && selectedIndex.name.toLowerCase().includes('esa cci')) {
                    return 2019;
                }
            }

            if (selectedIndex && selectedIndex.available_to) {
                const toDate = new Date(selectedIndex.available_to);
                return toDate.getFullYear();
            }
            return 2022;
        } catch (error) {
            console.error('Error getting available_to:', error);
            return 2022;
        }
    };

    const handleIndicatorMatrixCellChange = (rowKey, colIndex, value) => {
        const matrix = progressLandCoverIndicatorMatrix || initialIndicatorMatrix;
        const newMatrix = {
            ...matrix,
            [rowKey]: matrix[rowKey].map(
                (v, i) => i === colIndex ? parseInt(value) : v
            )
        };
        dispatch(setProgressLandCoverIndicatorMatrix(newMatrix));
    };

    // Get allIndexes from localStorage - memoized for performance
    const allIndexes = React.useMemo(() => {
        try {
            const categoriesStr = localStorage.getItem('categories');
            if (!categoriesStr) return [{ id: 'ndvi', name: 'MODIS Yearly NDVI', key: 'ndvi' }];
            const categories = JSON.parse(categoriesStr);
            return categories.map(category => ({
                id: category.id,
                name: category.name,
                key: category.id,
                type: category.type
            }));
        } catch (error) {
            console.error('Error parsing categories:', error);
            return [{ id: 'ndvi', name: 'MODIS Yearly NDVI', key: 'ndvi' }];
        }
    }, []);

    const landCoverIndexes = React.useMemo(() =>
        allIndexes.filter(index =>
            index.name.toLowerCase().includes('landcover') ||
            index.name.toLowerCase().includes('land cover') ||
            index.type === 'landcover'
        ), [allIndexes]
    );

    // Initialize default indexes - runs only ONCE on mount
    useEffect(() => {
        console.log('[ProgressConfig] Initializing default indexes...');

        if (!progressProductivityIndex && allIndexes.length > 0) {
            const defaultIndex = allIndexes.find(index =>
                index.name.toLowerCase().includes("yearly ndvi")
            ) || allIndexes[0];
            console.log('[ProgressConfig] Setting default productivity index:', defaultIndex.id);
            dispatch(setProgressProductivityIndex(defaultIndex.id));
        }

        if (!progressLandCoverIndex && landCoverIndexes.length > 0) {
            const defaultLandCover = landCoverIndexes.find(index =>
                index.name.toLowerCase().includes('esa cci')
            ) || landCoverIndexes[0];
            console.log('[ProgressConfig] Setting default land cover indexes:', defaultLandCover.id);

            dispatch(setProgressLandCoverIndex(defaultLandCover.id));
            dispatch(setProgressProductivityLandCover(defaultLandCover.id));
            dispatch(setProgressSoilOrganicIndex(defaultLandCover.id));
            dispatch(setProgressSoilOrganicLandCoverIndex(defaultLandCover.id));
        }
    }, []); // Empty dependency array - runs only once

    // Initialize default years - runs only ONCE on mount
    useEffect(() => {
        console.log('[ProgressConfig] Initializing default years...');
        console.log('[ProgressConfig] Current years:', {
            productivity: { from: progressProductivityYearFrom, to: progressProductivityYearTo },
            landCover: { from: progressLandCoverYearFrom, to: progressLandCoverYearTo },
            soilOrganic: { from: progressSoilOrganicYearFrom, to: progressSoilOrganicYearTo }
        });

        // Set default productivity years
        if (!progressProductivityYearFrom || !progressProductivityYearTo) {
            console.log('[ProgressConfig] Setting default productivity years: 2005-2019');
            dispatch(setProgressYears({
                type: 'productivity',
                from: 2005,
                to: 2019
            }));
        }

        // Set default land cover years
        if (!progressLandCoverYearFrom || !progressLandCoverYearTo) {
            console.log('[ProgressConfig] Setting default land cover years: 2015-2019');
            dispatch(setProgressYears({
                type: 'landcover',
                from: 2015,
                to: 2019
            }));
        }

        // Set default soil organic years
        if (!progressSoilOrganicYearFrom || !progressSoilOrganicYearTo) {
            console.log('[ProgressConfig] Setting default soil organic years: 2015-2019');
            dispatch(setProgressYears({
                type: 'soilorganic',
                from: 2015,
                to: 2019
            }));
        }
    }, []); // Empty dependency array - runs only once

    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showLandCoverAdvanced, setShowLandCoverAdvanced] = useState(false);
    const [showSoilOrganicAdvanced, setShowSoilOrganicAdvanced] = useState(false);
    const [showSoilOrganicLandCoverAdvanced, setShowSoilOrganicLandCoverAdvanced] = useState(false);

    const progressProductivityAdvanced = useSelector((state) => state.landDegradation.progressProductivityAdvanced);
    const progressLandCoverAdvanced = useSelector((state) => state.landDegradation.progressLandCoverAdvanced);
    const progressSoilOrganicAdvanced = useSelector((state) => state.landDegradation.progressSoilOrganicAdvanced);
    const progressSoilOrganicLandCoverAdvanced = useSelector((state) => state.landDegradation.progressSoilOrganicLandCoverAdvanced);

    useEffect(() => {
        dispatch(setProgressAdvancedEnabled(showAdvanced));
    }, [showAdvanced, dispatch]);

    useEffect(() => {
        dispatch(setProgressLandCoverAdvancedEnabled(showLandCoverAdvanced));
    }, [showLandCoverAdvanced, dispatch]);

    useEffect(() => {
        dispatch(setProgressSoilOrganicAdvancedEnabled(showSoilOrganicAdvanced));
        if (!showSoilOrganicAdvanced) {
            // When SOC is disabled, also disable Land Cover upload
            setShowSoilOrganicLandCoverAdvanced(false);
        }
    }, [showSoilOrganicAdvanced, dispatch]);

    useEffect(() => {
        dispatch(setProgressSoilOrganicLandCoverAdvancedEnabled(showSoilOrganicLandCoverAdvanced));
    }, [showSoilOrganicLandCoverAdvanced, dispatch]);

    const handleCopyFromBaseline = () => {
        const baselineMatrix = store.getState().landDegradation.landCoverIndicatorMatrix;
        if (baselineMatrix) {
            dispatch(setProgressLandCoverIndicatorMatrix(baselineMatrix));
            toast.success("Copied from baseline");
        } else {
            toast.warn("No baseline matrix available to copy");
        }
    };

    const handleFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setProgressProductivityAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMatrixChange = (key, values) => {
        const parsedValues = values.split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        if (parsedValues.length > 20) {
            toast.error("Maximum 20 values allowed");
            return;
        }

        const newMatrix = {
            ...progressProductivityAdvanced.transformationMatrix,
            [key]: parsedValues
        };
        dispatch(setProgressTransformationMatrix(newMatrix));
    };

    const handleLandCoverFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setProgressLandCoverAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSoilOrganicFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setProgressSoilOrganicAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSoilOrganicLandCoverFileUpload = (type) => (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                dispatch(setProgressSoilOrganicLandCoverAdvanced({
                    [type]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLandCoverMatrixChange = (key, values) => {
        const parsedValues = values.split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        if (parsedValues.length > 20) {
            toast.error("Maximum 20 values allowed");
            return;
        }

        const newMatrix = {
            ...progressLandCoverAdvanced.transformationMatrix,
            [key]: parsedValues
        };
        dispatch(setProgressLandCoverTransformationMatrix(newMatrix));
    };

    const handleSoilOrganicMatrixChange = (key, values) => {
        const parsedValues = values.split(',')
            .map(num => num.trim())
            .filter(num => num !== '')
            .map(num => parseInt(num))
            .filter(num => !isNaN(num) && Number.isInteger(num));

        if (parsedValues.length > 20) {
            toast.error("Maximum 20 values allowed");
            return;
        }

        const newMatrix = {
            ...progressSoilOrganicLandCoverAdvanced.transformationMatrix,
            [key]: parsedValues
        };
        dispatch(setProgressSoilOrganicLandCoverTransformationMatrix(newMatrix));
    };

    const matrix = progressLandCoverIndicatorMatrix || initialIndicatorMatrix;

    return (
        <div className="land-degradation-config">
            <Typography className="config-title" variant="h5" gutterBottom>Land Degradation</Typography>
            <Typography className="config-title" variant="h6" gutterBottom>Progress Period</Typography>

            {/* Progress Period - Productivity Index Section */}
            <div className="config-section">
                <Typography variant="h6" gutterBottom>
                    Progress Period - Productivity Index
                </Typography>
                <div className="input-group">
                    <Autocomplete
                        size="medium"
                        disablePortal={false}
                        options={allIndexes}
                        value={progressProductivityIndex
                            ? allIndexes.find(index => index.id === progressProductivityIndex)
                            : allIndexes.find(index => index.name.toLowerCase().includes("yearly ndvi")) || allIndexes[0]
                        }
                        getOptionLabel={(option) => option?.name || ""}
                        onChange={(_, value) => dispatch(setProgressProductivityIndex(value?.id))}
                        renderInput={(params) => <TextField {...params} label="Select Productivity Index" />}
                    />
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            type="number"
                            label="Decimals"
                            defaultValue={1}
                            value={progressProductivityDecimals}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                // Allow empty string for typing
                                if (inputValue === '') {
                                    dispatch(setProgressProductivityDecimals(''));
                                    return;
                                }
                                const value = parseInt(inputValue, 10);
                                // Only update if valid integer between 0-6
                                if (!isNaN(value) && value >= 0 && value <= 6) {
                                    dispatch(setProgressProductivityDecimals(value));
                                }
                            }}
                            InputProps={{
                                inputProps: { min: 0 }
                            }}
                            size="medium"
                            sx={{ width: '300px' }}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        {showAdvanced ? (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                    <TextField
                                        fullWidth
                                        type="file"
                                        label="First Land Cover"
                                        InputLabelProps={{ shrink: true }}
                                        onChange={handleFileUpload('firstLandCover')}
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        fullWidth
                                        type="file"
                                        label="Last Land Cover"
                                        InputLabelProps={{ shrink: true }}
                                        onChange={handleFileUpload('lastLandCover')}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        variant="text"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        startIcon={showAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        sx={{
                                            color: '#339ace',
                                            '&:hover': {
                                                backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                            }
                                        }}
                                    >
                                        Use From Portal's Land Cover
                                    </Button>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleOpenCopyDialog('productivity')}
                                        sx={{ color: '#339ace', borderColor: '#339ace' }}
                                    >
                                        Copy From
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleClearMatrix('productivity')}
                                        sx={{ color: '#339ace', borderColor: '#339ace' }}
                                    >
                                        Clear
                                    </Button>
                                </Box>

                                <Typography variant="subtitle2" gutterBottom>
                                    UNCCD Land Cover Transformation Matrix
                                </Typography>

                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>UNCCD Category</TableCell>
                                            <TableCell>Original Classes (comma-separated integers, max 20)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(progressProductivityAdvanced.transformationMatrix).map(([key, value]) => (
                                            <TableRow key={key}>
                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ color: '#339ace', fontWeight: 500 }}>
                                                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <MatrixRowInput
                                                        rowKey={key}
                                                        value={value}
                                                        onMatrixChange={(rowKey, parsedValues) => {
                                                            if (parsedValues.length > 20) {
                                                                toast.error("Maximum 20 values allowed");
                                                                return;
                                                            }
                                                            const newMatrix = {
                                                                ...progressProductivityAdvanced.transformationMatrix,
                                                                [rowKey]: parsedValues
                                                            };
                                                            dispatch(setProgressTransformationMatrix(newMatrix));
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Autocomplete
                                    size="medium"
                                    disablePortal={false}
                                    options={landCoverIndexes}
                                    value={progressProductivityLandCover
                                        ? landCoverIndexes.find(index => index.id === progressProductivityLandCover)
                                        : landCoverIndexes.find(index => index.name.toLowerCase().includes('esa cci')) || landCoverIndexes[0]
                                    }
                                    getOptionLabel={(option) => option?.name || ""}
                                    onChange={(_, value) => dispatch(setProgressProductivityLandCover(value?.id))}
                                    renderInput={(params) => <TextField {...params} label="Select Land Cover Index" />}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    startIcon={showAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Upload Land Cover
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Box className="year-picker-container">
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`progress-productivity-from-${progressProductivityLandCover}`}
                                setYear={(year) => dispatch(setProgressYears({ type: 'productivity', from: year }))}
                                yearFrom={getIndexAvailableFrom(progressProductivityLandCover)}
                                yearTo={getIndexAvailableTo(progressProductivityLandCover)}
                                label="Year From"
                                defValue={progressProductivityYearFrom || 2005}
                            />
                        </div>
                        <div style={{ width: "48%" }}>
                            <YearPicker
                                key={`progress-productivity-to-${progressProductivityLandCover}`}
                                setYear={(year) => dispatch(setProgressYears({
                                    type: 'productivity',
                                    to: year
                                }))}
                                yearFrom={getIndexAvailableFrom(progressProductivityLandCover)}
                                yearTo={getIndexAvailableTo(progressProductivityLandCover)}
                                label="Year To"
                                defValue={progressProductivityYearTo || 2019}
                            />
                        </div>
                    </Box>
                </div>
            </div>

            {/* Progress Period - Land Cover Section */}
            <div className="config-section">
                <Typography variant="h6" gutterBottom>
                    Progress Period - Land Cover
                </Typography>
                <div className="input-group">
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="text"
                            onClick={() => setShowLandCoverIndicatorMatrix(!showLandCoverIndicatorMatrix)}
                            startIcon={showLandCoverIndicatorMatrix ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            sx={{
                                color: '#339ace',
                                '&:hover': {
                                    backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                }
                            }}
                        >
                            {showLandCoverIndicatorMatrix ? 'Use Default Effects' : 'Define Effects of Land Cover Change'}
                        </Button>
                        <Box sx={{ alignItems: 'center', gap: 1, ml: 2, display: 'inline-flex' }}>
                            <InfoIcon sx={{ color: '#339ace', fontSize: '20px' }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                Optional
                            </Typography>
                        </Box>
                    </Box>

                    {showLandCoverIndicatorMatrix && (
                        <Box sx={{ mt: 2, mb: 3 }}>
                            <Box sx={{ display: 'flex', alignContent: 'center', gap: 3, justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', gap: 3, mb: 3, mt: 5, justifyContent: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, height: 20, bgcolor: '#4CAF50' }} />
                                        <Typography variant="body2">5 - Improvement</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, height: 20, bgcolor: '#FFFFFF', border: '1px solid #ccc' }} />
                                        <Typography variant="body2">4 - Stable</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, height: 20, bgcolor: '#9C27B0' }} />
                                        <Typography variant="body2">1 - Degradation</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3, mt: 5 }}>
                                    <Button variant='contained' onClick={handleCopyFromBaseline} sx={{ color: '#ffffff', ml: 2 }}>Copy from Baseline</Button>
                                    <Button variant='contained' onClick={handleResetIndicatorTable} sx={{ color: '#ffffff' }}>Reset Matrix</Button>
                                </Box>
                            </Box>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>From / To</TableCell>
                                        {Object.keys(matrix).map((key) => (
                                            <TableCell key={key} align="center">
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(matrix).map(([rowKey, rowValues]) => (
                                        <TableRow key={rowKey}>
                                            <TableCell>
                                                {rowKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </TableCell>
                                            {rowValues.map((value, colIndex) => (
                                                <TableCell key={colIndex} align="center" sx={{ p: 1 }}>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={value.toString()}
                                                        onChange={(e) => handleIndicatorMatrixCellChange(rowKey, colIndex, e.target.value)}
                                                        sx={{
                                                            width: '100px',
                                                            '& .MuiInputBase-input': {
                                                                bgcolor: getTransitionColor(value.toString()),
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="1">1</MenuItem>
                                                        <MenuItem value="4">4</MenuItem>
                                                        <MenuItem value="5">5</MenuItem>
                                                    </TextField>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {showLandCoverAdvanced ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="First Land Cover"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleLandCoverFileUpload('firstLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="Last Land Cover"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleLandCoverFileUpload('lastLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowLandCoverAdvanced(!showLandCoverAdvanced)}
                                    startIcon={showLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Use From Portal's Land Cover
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleOpenCopyDialog('landcover')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Copy From
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleClearMatrix('landcover')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Clear
                                </Button>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                                UNCCD Land Cover Transformation Matrix
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>UNCCD Category</TableCell>
                                        <TableCell>Original Classes (comma-separated integers, max 20)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(progressLandCoverAdvanced.transformationMatrix).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell>
                                                <Typography variant="subtitle2" sx={{ color: '#339ace', fontWeight: 500 }}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <MatrixRowInput
                                                    rowKey={key}
                                                    value={value}
                                                    onMatrixChange={(rowKey, parsedValues) => {
                                                        if (parsedValues.length > 20) {
                                                            toast.error("Maximum 20 values allowed");
                                                            return;
                                                        }
                                                        const newMatrix = {
                                                            ...progressLandCoverAdvanced.transformationMatrix,
                                                            [rowKey]: parsedValues
                                                        };
                                                        dispatch(setProgressLandCoverTransformationMatrix(newMatrix));
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Autocomplete
                                size="medium"
                                disablePortal={false}
                                options={landCoverIndexes}
                                value={progressLandCoverIndex
                                    ? landCoverIndexes.find(index => index.id === progressLandCoverIndex)
                                    : landCoverIndexes.find(index => index.name.toLowerCase().includes('esa cci')) || landCoverIndexes[0]
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                onChange={(_, value) => dispatch(setProgressLandCoverIndex(value?.id))}
                                renderInput={(params) => <TextField {...params} label="Select Land Cover Index" />}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="text"
                                onClick={() => setShowLandCoverAdvanced(!showLandCoverAdvanced)}
                                startIcon={showLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                sx={{
                                    color: '#339ace',
                                    '&:hover': {
                                        backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                    }
                                }}
                            >
                                Upload Land Cover
                            </Button>
                        </Box>
                    )}
                    <Box className="year-picker-container">
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`progress-landcover-from-${progressLandCoverIndex}`}
                                setYear={(year) => dispatch(setProgressYears({ type: 'landcover', from: year }))}
                                yearFrom={getIndexAvailableFrom(progressLandCoverIndex)}
                                yearTo={getIndexAvailableTo(progressLandCoverIndex)}
                                label="Year From"
                                defValue={progressLandCoverYearFrom || 2015}
                            />
                        </div>
                        <div style={{ width: "48%" }}>
                            <YearPicker
                                key={`progress-landcover-to-${progressLandCoverIndex}`}
                                setYear={(year) => dispatch(setProgressYears({
                                    type: 'landcover',
                                    to: year
                                }))}
                                yearFrom={getIndexAvailableFrom(progressLandCoverIndex)}
                                yearTo={getIndexAvailableTo(progressLandCoverIndex)}
                                label="Year To"
                                defValue={progressLandCoverYearTo || 2019}
                            />
                        </div>
                    </Box>
                </div>
            </div>

            {/* Progress Period - Soil Organic Carbon Section */}
            <div className="config-section">
                <Typography variant="h6" gutterBottom>
                    Progress Period - Soil Organic Carbon
                </Typography>
                <div className="input-group">
                    {showSoilOrganicAdvanced ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="First Soil Organic Carbon Index (in tonnes per hectare)"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicFileUpload('firstSoilOrganic')}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="Last Soil Organic Carbon Index (in tonnes per hectare)"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicFileUpload('lastSoilOrganic')}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowSoilOrganicAdvanced(!showSoilOrganicAdvanced)}
                                    startIcon={showSoilOrganicAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                                variant="text"
                                onClick={() => setShowSoilOrganicAdvanced(!showSoilOrganicAdvanced)}
                                startIcon={showSoilOrganicAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                sx={{
                                    color: '#339ace',
                                    '&:hover': {
                                        backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                    }
                                }}
                            >
                                Upload Soil Organic Carbon Index
                            </Button>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InfoIcon sx={{ color: '#339ace', fontSize: '20px' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                    Optional
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <br />
                    {showSoilOrganicLandCoverAdvanced ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="First Land Cover Index"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicLandCoverFileUpload('firstSoilOrganicLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    type="file"
                                    label="Last Land Cover Index"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSoilOrganicLandCoverFileUpload('lastSoilOrganicLandCover')}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="text"
                                    onClick={() => setShowSoilOrganicLandCoverAdvanced(!showSoilOrganicLandCoverAdvanced)}
                                    startIcon={showSoilOrganicLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Use From Portal's Land Cover
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleOpenCopyDialog('soilorganic')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Copy From
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleClearMatrix('soilorganic')}
                                    sx={{ color: '#339ace', borderColor: '#339ace' }}
                                >
                                    Clear
                                </Button>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                                UNCCD Land Cover Transformation Matrix
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>UNCCD Category</TableCell>
                                        <TableCell>Original Classes (comma-separated integers, max 20)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(progressSoilOrganicLandCoverAdvanced.transformationMatrix).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell>
                                                <Typography variant="subtitle2" sx={{ color: '#339ace', fontWeight: 500 }}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <MatrixRowInput
                                                    rowKey={key}
                                                    value={value}
                                                    onMatrixChange={(rowKey, parsedValues) => {
                                                        if (parsedValues.length > 20) {
                                                            toast.error("Maximum 20 values allowed");
                                                            return;
                                                        }
                                                        const newMatrix = {
                                                            ...progressSoilOrganicLandCoverAdvanced.transformationMatrix,
                                                            [rowKey]: parsedValues
                                                        };
                                                        dispatch(setProgressSoilOrganicLandCoverTransformationMatrix(newMatrix));
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Autocomplete
                                size="medium"
                                disablePortal={false}
                                options={landCoverIndexes}
                                value={progressSoilOrganicLandCoverIndex
                                    ? landCoverIndexes.find(index => index.id === progressSoilOrganicLandCoverIndex)
                                    : landCoverIndexes.find(index => index.name.toLowerCase().includes('esa cci')) || landCoverIndexes[0]
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                onChange={(_, value) => dispatch(setProgressSoilOrganicLandCoverIndex(value?.id))}
                                renderInput={(params) => <TextField {...params} label="Select Land Cover Index" />}
                                sx={{ flex: 1 }}
                            />
                            {showSoilOrganicAdvanced ? (
                                <Button
                                    variant="text"
                                    onClick={() => setShowSoilOrganicLandCoverAdvanced(!showSoilOrganicLandCoverAdvanced)}
                                    startIcon={showSoilOrganicLandCoverAdvanced ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#339ace',
                                        '&:hover': {
                                            backgroundColor: 'rgba(51, 154, 206, 0.08)'
                                        }
                                    }}
                                >
                                    Upload Land Cover
                                </Button>
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                    Enable SOC upload to use custom Land Cover
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Box className="year-picker-container">
                        <div className="year-picker-wrapper">
                            <YearPicker
                                key={`progress-soilorganic-from-${progressSoilOrganicLandCoverIndex}`}
                                setYear={(year) => dispatch(setProgressYears({ type: 'soilorganic', from: year }))}
                                yearFrom={getIndexAvailableFrom(progressSoilOrganicLandCoverIndex)}
                                yearTo={getIndexAvailableTo(progressSoilOrganicLandCoverIndex)}
                                label="Year From"
                                defValue={progressSoilOrganicYearFrom || 2015}
                            />
                        </div>
                        <div style={{ width: "48%" }}>
                            <YearPicker
                                key={`progress-soilorganic-to-${progressSoilOrganicLandCoverIndex}`}
                                setYear={(year) => dispatch(setProgressYears({
                                    type: 'soilorganic',
                                    to: year
                                }))}
                                yearFrom={getIndexAvailableFrom(progressSoilOrganicLandCoverIndex)}
                                yearTo={getIndexAvailableTo(progressSoilOrganicLandCoverIndex)}
                                label="Year To"
                                defValue={progressSoilOrganicYearTo || 2019}
                            />
                        </div>
                    </Box>
                </div>
            </div>

            {/* Copy From Dialog */}
            <Dialog open={copyFromDialogOpen} onClose={() => setCopyFromDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Copy Transformation Matrix From</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Select Source</InputLabel>
                        <Select
                            value={copySource}
                            onChange={(e) => setCopySource(e.target.value)}
                            label="Select Source"
                        >
                            <MenuItem value="baseline-productivity">Productivity Index (Baseline)</MenuItem>
                            <MenuItem value="baseline-landcover">Land Cover (Baseline)</MenuItem>
                            <MenuItem value="baseline-soilorganic">Soil Organic Carbon (Baseline)</MenuItem>
                            <MenuItem value="progress-productivity">Productivity Index (Progress)</MenuItem>
                            <MenuItem value="progress-landcover">Land Cover (Progress)</MenuItem>
                            <MenuItem value="progress-soilorganic">Soil Organic Carbon (Progress)</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                        <Button onClick={() => setCopyFromDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCopyMatrix}>Copy</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default ProgressConfig;